import React, { useEffect, useRef, useState } from "react";
import client, { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Download, Upload, Package, GitBranch, Globe, Table2, Shuffle, CheckCircle } from "lucide-react";

export default function ExportImportPage() {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({ sequences: new Set(), api_definitions: new Set(), lookup_tables: new Set(), pipelines: new Set() });
  const [bundleName, setBundleName] = useState("exceltoapi_export");
  const [exporting, setExporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const importRef = useRef();

  const TYPE_META = {
    sequences: { icon: GitBranch, label: "Execution Sequences", color: "#006080" },
    api_definitions: { icon: Globe, label: "API Definitions", color: "#2563EB" },
    lookup_tables: { icon: Table2, label: "Lookup Tables", color: "#D97706" },
    pipelines: { icon: Shuffle, label: "Transformation Pipelines", color: "#7C3AED" },
  };

  useEffect(() => {
    client.get("/export-import/preview").then(r => { setItems(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const toggleAll = (type, checked) => {
    setSelected(s => ({
      ...s,
      [type]: checked ? new Set((items[type] || []).map(i => i._id)) : new Set(),
    }));
  };

  const toggle = (type, id) => {
    setSelected(s => {
      const n = new Set(s[type]);
      n.has(id) ? n.delete(id) : n.add(id);
      return { ...s, [type]: n };
    });
  };

  const doExport = async () => {
    setExporting(true);
    try {
      const payload = {
        bundle_name: bundleName,
        include_sequences: selected.sequences.size > 0,
        include_api_definitions: selected.api_definitions.size > 0,
        include_lookup_tables: selected.lookup_tables.size > 0,
        include_pipelines: selected.pipelines.size > 0,
        sequence_ids: [...selected.sequences],
        api_def_ids: [...selected.api_definitions],
        lookup_ids: [...selected.lookup_tables],
        pipeline_upload_ids: [...selected.pipelines],
      };
      const res = await client.post("/export-import/export", payload, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${bundleName}.json`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { alert(formatApiError(e)); }
    finally { setExporting(false); }
  };

  const doImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const text = await file.text();
    try {
      const json = JSON.parse(text);
      const res = await client.post("/export-import/import", json);
      setImportResult(res.data);
    } catch (e) { alert(formatApiError(e)); }
    e.target.value = "";
  };

  const totalSelected = Object.values(selected).reduce((sum, s) => sum + s.size, 0);

  return (
    <div className="p-6 max-w-4xl mx-auto fade-in">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-1"><Package size={18} className="text-[#006080]" /><h1 className="text-2xl font-bold text-gray-900">Export / Import</h1></div>
        <p className="text-gray-500 text-sm">Migrate sequences, API definitions, lookup tables, and pipelines between instances as a single JSON bundle</p>
      </div>

      {importResult && (
        <div className="mb-5 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-2 mb-2"><CheckCircle size={16} className="text-green-600" /><p className="font-semibold text-green-700">Import successful!</p></div>
          <div className="flex gap-4 text-sm text-green-600">
            {Object.entries(importResult.created || {}).map(([k, v]) => v > 0 && (
              <span key={k}><strong>{v}</strong> {k.replace(/_/g, " ")}</span>
            ))}
          </div>
          <button className="text-xs text-green-600 underline mt-1" onClick={() => setImportResult(null)}>Dismiss</button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Export panel */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h2 className="font-bold text-gray-900 mb-1 flex items-center gap-2"><Download size={16} className="text-[#006080]" /> Export Bundle</h2>
          <p className="text-xs text-gray-400 mb-4">Select items to include. B = full bundle (all referenced deps) is pre-selected.</p>

          <div className="mb-4">
            <Label className="text-xs text-gray-500">Bundle File Name</Label>
            <Input className="mt-1 h-8 text-sm" value={bundleName} onChange={e => setBundleName(e.target.value)} placeholder="my_export" />
          </div>

          {loading ? <div className="text-sm text-gray-400">Loading items...</div>
          : Object.entries(TYPE_META).map(([type, meta]) => {
            const Icon = meta.icon;
            const typeItems = items?.[type] || [];
            const allSelected = typeItems.length > 0 && selected[type].size === typeItems.length;
            return (
              <div key={type} className="mb-4">
                <div className="flex items-center gap-2 mb-1.5">
                  <input type="checkbox" className="w-3.5 h-3.5" checked={allSelected} onChange={e => toggleAll(type, e.target.checked)} />
                  <Icon size={13} style={{ color: meta.color }} />
                  <span className="text-xs font-semibold text-gray-700">{meta.label}</span>
                  <span className="text-xs text-gray-400">({typeItems.length})</span>
                  {selected[type].size > 0 && <Badge className="text-[10px] ml-auto" style={{ background: `${meta.color}20`, color: meta.color }}>{selected[type].size} selected</Badge>}
                </div>
                <div className="ml-5 space-y-0.5 max-h-28 overflow-y-auto">
                  {typeItems.map(item => (
                    <label key={item._id} className="flex items-center gap-2 cursor-pointer py-0.5">
                      <input type="checkbox" className="w-3 h-3" checked={selected[type].has(item._id)} onChange={() => toggle(type, item._id)} />
                      <span className="text-xs text-gray-600 truncate">{item.name || item.filename || item._id}</span>
                      {item.method && <span className="text-[9px] px-1 rounded font-bold text-white" style={{ background: "#2563EB" }}>{item.method}</span>}
                      {item.row_count && <span className="text-[9px] text-gray-400">{item.row_count} rows</span>}
                    </label>
                  ))}
                  {typeItems.length === 0 && <p className="text-xs text-gray-300 italic">None available</p>}
                </div>
              </div>
            );
          })}

          <Button
            className="w-full mt-3 bg-[#006080] hover:bg-[#004d66] text-white"
            onClick={doExport}
            disabled={exporting || totalSelected === 0}
          >
            <Download size={14} className="mr-2" />
            {exporting ? "Exporting..." : `Export ${totalSelected} item(s) as JSON`}
          </Button>
        </div>

        {/* Import panel */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h2 className="font-bold text-gray-900 mb-1 flex items-center gap-2"><Upload size={16} className="text-[#006080]" /> Import Bundle</h2>
          <p className="text-xs text-gray-400 mb-4">Import a previously exported JSON bundle. IDs are remapped automatically — no conflicts.</p>

          <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center hover:border-[#006080] transition-colors cursor-pointer" onClick={() => importRef.current?.click()}>
            <Package size={32} className="mx-auto text-gray-300 mb-3" />
            <p className="text-sm font-medium text-gray-600">Drop bundle JSON here or click to browse</p>
            <p className="text-xs text-gray-400 mt-1">Accepts .json files exported from ExcelToAPI</p>
          </div>
          <input ref={importRef} type="file" accept=".json" className="hidden" onChange={doImport} />

          <div className="mt-5 space-y-2 text-xs text-gray-500">
            <p className="font-semibold text-gray-700">What gets imported:</p>
            <div className="space-y-1.5 ml-2">
              <p>✓ Execution sequences (steps, header injections, overrides)</p>
              <p>✓ API definitions (credentials re-encrypted at destination)</p>
              <p>✓ Lookup tables (key-value and range)</p>
              <p>✓ Pipeline templates (transform steps)</p>
              <p className="text-gray-400">— Internal IDs are remapped automatically</p>
              <p className="text-gray-400">— Original items at source are unchanged</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
